import React, { createContext, useContext, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import supabase from '../services/supabaseClient'

const AuthContext = createContext()

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    // Check active session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user)
        fetchProfile(session.user.id)
      } else {
        setLoading(false)
      }
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(session.user)
        fetchProfile(session.user.id)
      } else {
        setUser(null)
        setProfile(null)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  async function fetchProfile(authId) {
    try {
      console.log('Fetching profile for authId:', authId)
      
      // First get user without schools join
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('auth_id', authId)
        .single()

      if (userError) {
        console.error('User query error:', userError)
        throw userError
      }
      
      console.log('User data found:', userData)
      
      // If user has a school, fetch school data separately
      let profileData = userData
      if (userData.school_id) {
        const { data: schoolData, error: schoolError } = await supabase
          .from('schools')
          .select('*')
          .eq('id', userData.school_id)
          .single()
        
        if (!schoolError && schoolData) {
          profileData = { ...userData, schools: schoolData }
        }
      }
      
      setProfile(profileData)
    } catch (error) {
      console.error('Error fetching profile:', error)
      alert('Database error: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  async function signIn(email, password) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }

  async function signUp(email, password, userData) {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
        },
      })
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }

  async function signOut() {
    try {
      await supabase.auth.signOut()
      navigate('/login')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  // Role-based helpers
  const isSuperAdmin = profile?.role === 'super_admin'
  const isSchoolAdmin = profile?.role === 'school_admin'
  const isTeacher = profile?.role === 'teacher'
  const isParent = profile?.role === 'parent'
  const isStudent = profile?.role === 'student'

  // Get dashboard route based on role
  const getDashboardRoute = () => {
    switch (profile?.role) {
      case 'super_admin':
        return '/admin/super'
      case 'school_admin':
        return '/admin/school'
      case 'teacher':
        return '/teacher'
      case 'parent':
        return '/parent'
      case 'student':
        return '/student'
      default:
        return '/'
    }
  }

  const value = {
    user,
    profile,
    loading,
    signIn,
    signUp,
    signOut,
    isSuperAdmin,
    isSchoolAdmin,
    isTeacher,
    isParent,
    isStudent,
    getDashboardRoute,
    refreshProfile: () => fetchProfile(user?.id),
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}